import * as THREE from "three";
import { sparkleMaterial } from "./views/rareShiny";

/**
 * The Starfall Wyrm's sky (M40, "lots of sparkles, over the top drama"): while it lives the air over the map fills with
 * twinkling stars and shooting stars streak down through them — lilac, rose-gold and cyan comets with white heads —
 * with a whole shower as it dives in (`shower`). One `THREE.Points` draw (rareShiny.ts sparkleMaterial: additive, a
 * procedural four-point star, no texture), `Stars` + `Meteors` × `Tail` sprites updated on the CPU in place (no
 * per-frame allocation), never drawn while its weight is 0 or under reduced effects.
 */
export class WyrmSky {
  static readonly Stars = 96;
  static readonly Meteors = 8;
  /** Sprites per meteor: its head and a tapering tail. */
  static readonly Tail = 12;
  /** The band of air the stars hang in (cells over the ground) — high enough to sit over the towers in the iso view. */
  static readonly StarLow = 3.6;
  static readonly StarHigh = 8.5;
  /** A meteor's speed (cells/s), life (s), the spacing of its tail sprites (s of travel) and the pause between two. */
  static readonly Speed = 11;
  static readonly Life = 0.85;
  static readonly TailStep = 0.018;
  static readonly GapMin = 0.25;
  static readonly GapMax = 1.6;

  readonly points: THREE.Points;
  private readonly geo = new THREE.BufferGeometry();
  private readonly mat = sparkleMaterial();
  private readonly pos: Float32Array;
  private readonly col: Float32Array;
  private readonly size: Float32Array;
  /** Stars: x, y, z, phase. */
  private readonly star = new Float32Array(WyrmSky.Stars * 4);
  /** Meteors: start x, y, z, velocity x, y, z, age (−delay while waiting), colour index. */
  private readonly met = new Float32Array(WyrmSky.Meteors * 8);
  private readonly palette: THREE.Color[];
  private readonly white = new THREE.Color(0xffffff);
  private time = 0;

  /** `w`×`h` cells: the map the sky hangs over (and a margin around it). Colours: the Wyrm's three (0xRRGGBB). */
  constructor(private readonly w: number, private readonly h: number, colours: readonly number[]) {
    const n = WyrmSky.Stars + WyrmSky.Meteors * WyrmSky.Tail;
    this.pos = new Float32Array(n * 3); this.col = new Float32Array(n * 3); this.size = new Float32Array(n);
    this.geo.setAttribute("position", new THREE.BufferAttribute(this.pos, 3).setUsage(THREE.DynamicDrawUsage));
    this.geo.setAttribute("color", new THREE.BufferAttribute(this.col, 3).setUsage(THREE.DynamicDrawUsage));
    this.geo.setAttribute("size", new THREE.BufferAttribute(this.size, 1).setUsage(THREE.DynamicDrawUsage));
    this.geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(w / 2, 4, h / 2), Math.hypot(w, h) + 10);
    this.points = new THREE.Points(this.geo, this.mat);
    this.points.frustumCulled = false;
    this.points.renderOrder = 4;
    this.points.visible = false;
    this.palette = colours.map((c) => new THREE.Color(c));
    for (let i = 0; i < WyrmSky.Stars; i++) {
      const k = i * 4;
      this.star[k] = -3 + Math.random() * (w + 6);
      this.star[k + 1] = WyrmSky.StarLow + Math.random() * (WyrmSky.StarHigh - WyrmSky.StarLow);
      this.star[k + 2] = -3 + Math.random() * (h + 6);
      this.star[k + 3] = Math.random();
      const p = i * 3;
      this.pos[p] = this.star[k]; this.pos[p + 1] = this.star[k + 1]; this.pos[p + 2] = this.star[k + 2];
    }
    for (let m = 0; m < WyrmSky.Meteors; m++) this.arm(m, Math.random() * WyrmSky.GapMax);
  }

  /** A meteor waits `delay` s, then falls from high over the map toward its far side. */
  private arm(m: number, delay: number): void {
    const k = m * 8, S = WyrmSky.Speed;
    const dx = -0.55 + Math.random() * 0.25, dz = 0.35 + Math.random() * 0.25, dy = -1;
    const len = Math.hypot(dx, dy, dz);
    this.met[k] = Math.random() * (this.w + 4);
    this.met[k + 1] = WyrmSky.StarHigh + 1 + Math.random() * 2;
    this.met[k + 2] = -4 + Math.random() * (this.h * 0.7);
    this.met[k + 3] = (dx / len) * S; this.met[k + 4] = (dy / len) * S * 0.55; this.met[k + 5] = (dz / len) * S;
    this.met[k + 6] = -delay;
    this.met[k + 7] = Math.floor(Math.random() * this.palette.length);
  }

  /** A shower: every meteor falls within the next `within` seconds (the Wyrm's dive). */
  shower(within = 0.5): void {
    for (let m = 0; m < WyrmSky.Meteors; m++) this.arm(m, Math.random() * within);
  }

  /** Per frame with the night's weight (0..1, RareGlow's sparkle weight); nothing is drawn at 0 or `reduced`. */
  update(dt: number, weight: number, reduced: boolean): void {
    const on = weight > 0.01 && !reduced;
    this.points.visible = on;
    if (!on) return;
    this.time += dt;
    const t = this.time, wgt = Math.min(1, weight);
    for (let i = 0; i < WyrmSky.Stars; i++) {
      const ph = this.star[i * 4 + 3];
      const tw = Math.max(0, Math.sin(t * (1.6 + 3 * ph) + ph * 40));
      const k = (0.35 + 1.6 * tw * tw) * wgt;
      const c = i % 5 === 0 ? this.palette[i % this.palette.length] : this.white;
      this.col[i * 3] = c.r * k; this.col[i * 3 + 1] = c.g * k; this.col[i * 3 + 2] = c.b * k;
      this.size[i] = (0.07 + 0.08 * tw) * (i % 7 === 0 ? 1.8 : 1);
    }
    for (let m = 0; m < WyrmSky.Meteors; m++) this.meteor(m, dt, wgt);
    (this.geo.attributes.position as THREE.BufferAttribute).needsUpdate = true;
    (this.geo.attributes.color as THREE.BufferAttribute).needsUpdate = true;
    (this.geo.attributes.size as THREE.BufferAttribute).needsUpdate = true;
  }

  private meteor(m: number, dt: number, wgt: number): void {
    const k = m * 8, T = WyrmSky.Tail, base = WyrmSky.Stars + m * T;
    this.met[k + 6] += dt;
    const age = this.met[k + 6];
    if (age > WyrmSky.Life) { this.arm(m, WyrmSky.GapMin + Math.random() * (WyrmSky.GapMax - WyrmSky.GapMin)); }
    const live = age >= 0 && age <= WyrmSky.Life;
    const fade = live ? Math.sin(Math.PI * Math.min(1, age / WyrmSky.Life)) * wgt : 0;
    const tint = this.palette[this.met[k + 7] | 0] ?? this.white;
    for (let j = 0; j < T; j++) {
      const i = base + j, back = Math.max(0, age - j * WyrmSky.TailStep);
      this.pos[i * 3] = this.met[k] + this.met[k + 3] * back;
      this.pos[i * 3 + 1] = this.met[k + 1] + this.met[k + 4] * back;
      this.pos[i * 3 + 2] = this.met[k + 2] + this.met[k + 5] * back;
      const u = j / T, c = j === 0 ? this.white : tint, b = (j === 0 ? 3.2 : 2.2 * (1 - u)) * fade;
      this.col[i * 3] = c.r * b; this.col[i * 3 + 1] = c.g * b; this.col[i * 3 + 2] = c.b * b;
      this.size[i] = live ? (j === 0 ? 0.32 : 0.2 * (1 - u * 0.8)) : 0;
    }
  }

  dispose(): void { this.points.removeFromParent(); this.geo.dispose(); this.mat.dispose(); }
}
